import { useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { Avatar } from '../../../components/primitives/Avatar';
import { Badge } from '../../../components/primitives/Badge';
import { Button } from '../../../components/primitives/Button';
import { Input } from '../../../components/primitives/Input';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { useUpdateProfile } from '../hooks/useSettings';
import { SettingsSection } from './SettingsSection';

export function ProfileSection() {
  const { auth0User, backendUser } = useAuth();
  const { t } = useTranslation();
  const updateProfile = useUpdateProfile();

  const [fullName, setFullName] = useState(backendUser?.fullName ?? '');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const nameUnchanged = fullName.trim() === (backendUser?.fullName ?? '');

  const handleSave = () => {
    const trimmed = fullName.trim();
    if (!trimmed || nameUnchanged) return;

    setSaveStatus('idle');
    updateProfile.mutate(
      { fullName: trimmed },
      {
        onSuccess: () => {
          setSaveStatus('success');
          setTimeout(() => setSaveStatus('idle'), 3000);
        },
        onError: () => {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 4000);
        },
      },
    );
  };

  const roleBadgeColor = (() => {
    const role = backendUser?.role?.toLowerCase();
    if (role === 'admin') return 'purple' as const;
    if (role === 'manager') return 'blue' as const;
    return 'cyan' as const;
  })();

  return (
    <SettingsSection title={t('settings.profile.title')} boxStyle={styles.card}>
      <HStack spacing="lg" align="center">
        <Avatar
          size="xl"
          src={auth0User?.picture}
          name={backendUser?.fullName ?? auth0User?.name}
          accentRing
        />

        <VStack spacing="xs">
          <Text variant="body" weight="semibold" color={colors.text.primary}>
            {backendUser?.fullName ?? auth0User?.name ?? t('common.fallbacks.user')}
          </Text>
          {backendUser?.role && (
            <Badge variant="subtle" color={roleBadgeColor} size="sm">
              {backendUser.role}
            </Badge>
          )}
        </VStack>
      </HStack>

      <VStack spacing="lg">
        <Input
          label={t('settings.profile.fullName')}
          value={fullName}
          onChangeText={setFullName}
          placeholder={t('settings.profile.fullNamePlaceholder')}
          autoCapitalize="words"
          autoCorrect={false}
        />

        <Input
          label={t('settings.profile.email')}
          value={auth0User?.email ?? ''}
          editable={false}
          style={styles.readOnlyInput}
        />
      </VStack>

      {saveStatus === 'success' && (
        <Text variant="bodySmall" color={colors.status.success.main}>
          {t('settings.profile.successMessage')}
        </Text>
      )}
      {saveStatus === 'error' && (
        <Text variant="bodySmall" color={colors.status.error.main}>
          {t('settings.profile.errorMessage')}
        </Text>
      )}

      <Button
        variant="primary"
        onPress={handleSave}
        loading={updateProfile.isPending}
        disabled={nameUnchanged || !fullName.trim()}
        style={styles.saveButton}
      >
        {t('common.actions.saveChanges')}
      </Button>
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  card: {
    ...Platform.select({
      web: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.14)' } as any,
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 6,
      },
      android: { elevation: 2 },
    }),
  },
  readOnlyInput: {
    backgroundColor: colors.background.secondary,
    opacity: 0.7,
  },
  saveButton: {
    alignSelf: 'flex-start',
  },
});
